import HttpService from './HttpService';

const API_URL = '/houses';
const PROPERTY_INFO_URL = '/property-info';
const PLACES_URL = '/places';

/**
 * Format error for consistent handling across components
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 * @returns {Object} - Structured error with message and code
 */
const formatError = (error, defaultMessage) => {
  const data = error.response?.data;
  // Read both `message` (api_response) and `error` (rate-limiter shape),
  // and expose the structured extras — parity with RoomService.formatError.
  const errorMessage = data?.message || data?.error || defaultMessage;
  const errorCode = error.response?.status;
  const details = data?.details || {};
  const errors = Array.isArray(data?.errors) ? data.errors : [];
  // Only log errors in development to avoid exposing sensitive info in
  // production — parity with RoomService.formatError.
  if (process.env.NODE_ENV !== 'production') {
    console.error(defaultMessage, error);
  }
  return {
    message: errorMessage,
    code: errorCode,
    originalError: error,
    details,
    errors,
    error_code: data?.error_code,
  };
};

const HouseService = {
  /**
   * Get all houses for the current user
   * @returns {Promise<Array>} - List of houses
   */
  getHouses: async () => {
    try {
      const response = await HttpService.get(API_URL);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching houses');
    }
  },

  /**
   * Get a specific house by ID with full details
   * @param {number} id - House ID
   * @returns {Promise<Object>} - House details
   */
  getHouse: async (id) => {
    try {
      const response = await HttpService.get(`${API_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching house ${id}`);
    }
  },

  /**
   * Get the user's primary house. Returns null when the backend reports
   * 404 (no primary set) so the caller can branch UI without try/catch.
   * Other errors still propagate.
   *
   * Backend contract: GET /houses/primary returns 200 with the same shape
   * as GET /houses/:id, or 404 when no house is marked is_primary.
   *
   * @returns {Promise<Object|null>} - Primary house, or null if none set
   */
  getPrimaryHouse: async () => {
    try {
      const response = await HttpService.get(`${API_URL}/primary`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw formatError(error, 'Error fetching primary house');
    }
  },

  /**
   * Mark a house as the user's primary home. The backend atomically clears
   * the primary flag on every other house, so exactly one stays primary.
   *
   * Backend contract: POST /houses/:id/primary returns
   * { success, message, house }.
   *
   * @param {number} id - House ID to promote
   * @returns {Promise<Object>} - { success, message, house }
   */
  setPrimaryHouse: async (id) => {
    try {
      const response = await HttpService.post(`${API_URL}/${id}/primary`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error setting house ${id} as primary`);
    }
  },

  /**
   * Re-pull county property-tax history for a house on demand (e.g. a
   * "Refresh from county" button). The backend uses the house's stored parcel
   * id + address county/FIPS to query the matching county provider and upserts
   * any records it returns.
   *
   * Backend contract: POST /houses/:id/refresh-county-tax returns
   * { success, updated, source, house }. `updated` is false when no provider
   * serves the property or the county has no records (not an error).
   *
   * @param {number} id - House ID
   * @returns {Promise<Object>} - { success, updated, source, house }
   */
  refreshCountyTax: async (id) => {
    try {
      const response = await HttpService.post(`${API_URL}/${id}/refresh-county-tax`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error refreshing county tax for house ${id}`);
    }
  },

  /**
   * Get items at house scope (any room within the house, plus roomless items
   * that point to this house directly via Item.house_id).
   *
   * @param {number} id - House ID
   * @param {Object} [params] - Optional pagination params: { limit, offset }
   * @returns {Promise<Object>} - { items: [...], pagination: {...} }
   */
  getHouseItems: async (id, params = {}) => {
    try {
      const response = await HttpService.get(`${API_URL}/${id}/items`, { params });
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching items for house ${id}`);
    }
  },

  /**
   * Get house summary with aggregated statistics
   * @param {number} id - House ID
   * @returns {Promise<Object>} - House summary with stats
   */
  getHouseSummary: async (id) => {
    try {
      const response = await HttpService.get(`${API_URL}/${id}/summary`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching house summary ${id}`);
    }
  },

  /**
   * Create a new house
   * @param {FormData} formData - House data including files
   * @returns {Promise<Object>} - Created house
   */
  createHouse: async (formData) => {
    try {
      const response = await HttpService.post(API_URL, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating house');
    }
  },

  /**
   * Update an existing house
   * @param {number} id - House ID
   * @param {FormData} formData - House data including files
   * @returns {Promise<Object>} - Updated house
   */
  updateHouse: async (id, formData) => {
    try {
      const response = await HttpService.put(`${API_URL}/${id}`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating house ${id}`);
    }
  },

  /**
   * Upload a gallery photo for a house (stored in S3/R2)
   * @param {number} houseId - House ID
   * @param {File} file - Image file
   * @param {boolean} isPrimary - Whether to set as primary image
   * @returns {Promise<Object>} - Uploaded image data with presigned URL
   */
  uploadHouseImage: async (houseId, file, isPrimary = false) => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (isPrimary) formData.append('is_primary', 'true');
      const response = await HttpService.post(`${API_URL}/${houseId}/images`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error uploading house image');
    }
  },

  /**
   * Delete an image from a house
   * @param {number} houseId - House ID
   * @param {number} imageId - Image ID
   * @returns {Promise<Object>} - Deletion confirmation
   */
  deleteHouseImage: async (houseId, imageId) => {
    try {
      const response = await HttpService.delete(`${API_URL}/${houseId}/images/${imageId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error deleting house image');
    }
  },

  /**
   * Set an image as the primary image for a house
   * @param {number} houseId - House ID
   * @param {number} imageId - Image ID
   * @returns {Promise<Object>} - Updated image data
   */
  setHouseImagePrimary: async (houseId, imageId) => {
    try {
      const response = await HttpService.put(`${API_URL}/${houseId}/images/${imageId}/primary`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error setting primary image');
    }
  },

  /**
   * Fetch normalized property info via backend proxy.
   * The backend normalizes provider data into our house schema,
   * so the frontend receives provider-agnostic fields.
   * @param {string} address - Full address string
   * @returns {Promise<Object>} - { success, house } or { success: false, error }
   */
  getPropertyInfo: async (address) => {
    try {
      const response = await HttpService.get(PROPERTY_INFO_URL, {
        params: { address }
      });
      return response.data;
    } catch (error) {
      console.warn('Property info fetch failed', error);
      return { success: false, error: 'Failed to fetch property info' };
    }
  },

  /**
   * Get Google Places Autocomplete suggestions
   * @param {string} input - Search input
   * @returns {Promise<Object>} - Autocomplete predictions
   */
  getPlacesAutocomplete: async (input) => {
    try {
      const response = await HttpService.get(`${PLACES_URL}/autocomplete`, {
        params: { input }
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch places autocomplete", error);
      return { success: false, error: 'Failed to fetch suggestions' };
    }
  },

  /**
   * Get Google Places Details for a place ID
   * @param {string} placeId - Google Place ID
   * @returns {Promise<Object>} - Place details
   */
  getPlaceDetails: async (placeId) => {
    try {
      const response = await HttpService.get(`${PLACES_URL}/details`, {
        params: { place_id: placeId }
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch place details", error);
      return { success: false, error: 'Failed to fetch place details' };
    }
  },

  /**
   * Get configuration options for houses
   * @returns {Promise<Object>} - House configuration arrays
   */
  getConfig: async () => {
    try {
      const response = await HttpService.get(`${API_URL}/config`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching house configuration');
    }
  }
};

export default HouseService;
